import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterModule } from "@angular/router";

import { TranslateModule } from "@ngx-translate/core";
import { filter } from "rxjs";

import { ThemeSwitcherComponent } from "@/app/shared/components/theme-switcher/theme-switcher.component";
import { APP_PATHS, BREADCRUMB_SEGMENTS } from "@/app/shared/constants";
import { AbsoluteRoutePipe } from "@/app/shared/pipes/absolute-route.pipe";
import { ViewportService } from "@/app/shared/services/viewport.service";
import { environment } from "@/environments/environment";
import type { BreadcrumbSegment } from "../models";

/**
 * Header component displaying a responsive breadcrumb navigation.
 * Shows a drawer toggle on desktop and a warehouse home button on tablet
 * when navigating within warehouse sections.
 */
@Component({
  selector: "app-header",
  imports: [RouterModule, TranslateModule, ThemeSwitcherComponent, AbsoluteRoutePipe],
  templateUrl: "header.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly viewport = inject(ViewportService);

  readonly path = APP_PATHS;
  isDrawerOpen = input.required<boolean>();
  toggleDrawer = output<void>();
  isDevEnv = environment.production === false;

  private readonly currentBreadcrumb = signal(this.resolveBreadcrumb());
  private readonly currentSection = signal(this.resolveSection());

  readonly breadcrumbItems = computed<BreadcrumbSegment[]>(() => {
    const section = this.currentBreadcrumb();
    if (!section) return [];

    const parts = section.split("/");
    const items: BreadcrumbSegment[] = [];

    for (let i = 0; i < parts.length; i++) {
      const key = parts.slice(0, i + 1).join("/");
      const segment = BREADCRUMB_SEGMENTS[key];
      if (segment) {
        items.push({ ...segment });
      }
    }

    // Last item is current page — remove its route
    if (items.length > 0) {
      items[items.length - 1] = { label: items[items.length - 1].label };
    }

    return items;
  });

  readonly showWarehouseHome = computed(
    () => this.viewport.isTablet() && this.currentSection().startsWith("wms/warehouse"),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.currentBreadcrumb.set(this.resolveBreadcrumb());
        this.currentSection.set(this.resolveSection());
      });
  }

  private resolveRouteData(key: string): string {
    let route = this.router.routerState?.root;
    if (!route) return "";

    while (route.firstChild) {
      route = route.firstChild;
    }

    return (route.snapshot?.data[key] as string) ?? "";
  }

  private resolveBreadcrumb(): string {
    return this.resolveRouteData("breadcrumb") || this.resolveRouteData("section");
  }

  private resolveSection(): string {
    return this.resolveRouteData("section");
  }
}
